import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import Label from '../../../../incase-domain/components/label/Label';
import {IElement} from '../../../../incase-domain/elements';
import {IElementModal} from '../../../../incase-domain/elements/modal';
import {ISlideOption} from '../../../../incase-domain/slide';
import {ELEMENT_NAME_MAX} from '../../../../incase-domain/validators/elements/constant';
import {modalValidator} from '../../../../incase-domain/validators/elements/modalElement';
import {getElementClickBeforeDisplayOptions} from '../../../../utils/element';
import {getErrorClasses} from '../../../../utils/validation';
import SectionTitle from '../../../elements/common/SectionTitle';
import {TABS} from '../../form/part/tabs';
import DebouncedTextInput from '../common/debounced/DebouncedTextInput';
import ElementsToClickBeforeDisplay from '../common/elementsToClickBeforeAfter/elementsToClickBeforeDisplay';
import FontSelect from '../common/fontSelect';
import SlideAsContent from '../common/slideAsContent';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import {type TypedTranslation} from '../../../../i18n';
import {selectedSlideSelector} from '../../../../store/selectors/slide.selectors';

interface HighlightedModalElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

function HighlightedModalElementComponent(props: HighlightedModalElementComponentProps): ReactElement {
    const {errors, activeTab} = props;
    const {t} = useTranslation();
    const highlightedElement = useHighlightedElement() as IElementModal;
    const updateHighlightedElement = useSetHighlightedElement();
    const slide = useSelector(selectedSlideSelector);
    const slideElements = slide?.elements?.filter((slideElement) => slideElement.id && slideElement.id !== highlightedElement.id) || [];

    // elements to click before display
    const beforeElementOptions: ISlideOption[] = slideElements.reduce((reducedElements: ISlideOption[], slideElement: IElement) => {
        return [...reducedElements, ...getElementClickBeforeDisplayOptions(slideElement)];
    }, []);

    if (activeTab === TABS.ADVANCED.id) {
        return <></>;
    }

    return (
        <>
            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.content')} infoText={t('designSidepanel.modalElementDescription')} />
                <Label>{t('designSidepanel.title')}</Label>
                <DebouncedTextInput
                    className="c-sidepanel__input"
                    onChange={(value) => {
                        updateHighlightedElement.single('title', value);
                    }}
                    validator={(val) => {
                        const validation = modalValidator.shape.title.safeParse(val);
                        return validation.success;
                    }}
                    value={highlightedElement.title || t('designSidepanel.newTitle')}
                    maxLength={ELEMENT_NAME_MAX}
                />

                <div className={`l-group__item ${getErrorClasses(errors, `selectedSlideId`)}`}>
                    <SlideAsContent
                        selectedSlideId={highlightedElement.selectedSlideId}
                        highlightedElementChangeHandler={updateHighlightedElement}
                    />
                    {errors?.selectedSlideId && <div className="c-sidepanel__error">{t(errors.selectedSlideId as TypedTranslation)}</div>}
                </div>

                <div className={`l-group__item ${getErrorClasses(errors, `elementToClickForDisplay`)}`}>
                    <ElementsToClickBeforeDisplay
                        slideElementOptions={beforeElementOptions}
                        elementToClickForDisplay={highlightedElement.elementToClickForDisplay || []}
                        highlightedElementChangeHandler={updateHighlightedElement}
                    />
                    {errors?.elementToClickForDisplay && (
                        <div className="c-sidepanel__error">{t(errors.elementToClickForDisplay as TypedTranslation)}</div>
                    )}
                </div>
            </div>

            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.style')} />
                <FontSelect highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
            </div>
        </>
    );
}

export default HighlightedModalElementComponent;
